//
// Copyright (c) 2026 Comcast
//

import { Fragment } from 'react'
import PropTypes from 'prop-types'
import i18next from 'i18next'
import isArray from 'lodash/isArray'

import colors from 'components/utils/colors'

import SidebarSection from 'components/Shared/Sidebar/SidebarSection/Index'
import SidebarSectionGroup from 'components/Shared/Sidebar/SidebarSectionGroup/Index'
import SidebarText from 'components/Shared/Sidebar/SidebarText/Index'
import SidebarBadges from 'components/Shared/Sidebar/SidebarBadges/Index'
import SidebarSectionBool from 'components/Shared/Sidebar/SidebarSectionBool/Index'
import SidebarLink from 'components/Shared/Sidebar/SidebarLink/Index'
import SidebarBase64Image from 'components/Shared/Sidebar/SidebarBase64Image/Index'
import SidebarSectionGroupTitle from 'components/Shared/Sidebar/SidebarSectionGroupTitle/Index'
import SidebarSectionTable from 'components/Shared/Sidebar/SidebarSectionTable/Index'

import {
  RouteAdminStatusCollections,
  RouteAdminSharedFieldMaps,
  RouteAdminMimeTypeCollections,
  RouteAdminProviders, RouteAdminProviderCredentials,
} from 'routes'
import { userTimeZoneFormat } from 'utils/time'

// for different props of 'internal_link' type hardcodes tooltips and what URLs to open
const linkPropsMap = {
  'statusCollectionId': {
    tooltip: i18next.t('sidebar.sidebarSectionsBySchema.sectionStatusCollectionLinkTooltip'),
    linkMaker: (id) => RouteAdminStatusCollections({ statusCollectionId: id }),
  },
  'sharedFieldsMapId': {
    tooltip: i18next.t('sidebar.sidebarSectionsBySchema.sectionSharedMapLinkTooltip'),
    linkMaker: (id) => RouteAdminSharedFieldMaps({ sharedFieldsMapId: id }),
  },
  'mimeTypeCollectionId': {
    tooltip: i18next.t('sidebar.sidebarSectionsBySchema.sectionMimeTypeCollectionLinkTooltip'),
    linkMaker: (id) => RouteAdminMimeTypeCollections({ mimeTypeCollectionId: id }),
  },
  'ticketProviderId': {
    tooltip: i18next.t('sidebar.sidebarSectionsBySchema.sectionTicketProviderLinkTooltip'),
    linkMaker: (id) => RouteAdminProviders({ ticketProviderId: id }),
  },
  'providerCredentialId': {
    tooltip: i18next.t('sidebar.sidebarSectionsBySchema.sectionProviderCredentialLinkTooltip'),
    linkMaker: (id) => RouteAdminProviderCredentials({ providerCredentialId: id }),
  },
}

const getTextValue = (propSchemaEx, value) => {
  return (propSchemaEx.type === 'array' && isArray(value))
    ? value.join(', ')
    : (propSchemaEx.valuesMap ? propSchemaEx.valuesMap[value] : value)
}

const getTextSection = (propSchemaEx, propName, value) => {
  const showCopyIcon = propSchemaEx.format !== 'password'
  const textValue = getTextValue(propSchemaEx, value)

  return (
    <SidebarSection
      title={propSchemaEx.title}
      tooltipText={propSchemaEx.description}
      copyText={showCopyIcon ? textValue : undefined}
      key={propName}
    >
      <SidebarText text={textValue} />
    </SidebarSection>
  )
}

const getColorSection = (propSchemaEx, propName, value) => {
  const textValue = value || 'no data'

  return (
    <SidebarSection
      title={propSchemaEx.title}
      tooltipText={propSchemaEx.description}
      copyText={textValue}
      key={propName}
    >
      <SidebarBadges badges={[{ value: value, title: textValue, color: value || colors.secondary80 }]} />
    </SidebarSection>
  )
}

const getBadgesSection = (propSchemaEx, propName, value) => {
  const textValue = getTextValue(propSchemaEx, value)
  const badges = value.map(val => {
    return ({
      value: val,
      title: propSchemaEx.valuesMap[val],
      color: propSchemaEx.colorsMap?.[val],
    })
  })

  return (
    <SidebarSection
      title={propSchemaEx.title}
      tooltipText={propSchemaEx.description}
      copyText={textValue}
      key={propName}
    >
      {(badges.length > 0)
        ? <SidebarBadges badges={badges} />
        : <SidebarText text={null} />
      }
    </SidebarSection>
  )
}

const getImageSection = (propSchemaEx, propName, value) => {
  return (
    <SidebarSection title={propSchemaEx.title} key={propName}>
      <SidebarBase64Image
        base64Content={value?.base64Content}
        contentType={value?.contentType}
      />
    </SidebarSection>
  )
}

const getBoolSection = (propSchemaEx, propName, value) => {
  return (
    <SidebarSectionBool
      title={propSchemaEx.title}
      value={value}
      key={propName}
    />
  )
}

const getOneOfLinkSection = (propSchemaEx, propName, value) => {
  const textValue = propSchemaEx.valuesMap[value]
  const tooltip = linkPropsMap[propName].tooltip

  const renderSidebarLink = (textValue, value, tooltip) => {
    const linkTo = { pathname: linkPropsMap[propName].linkMaker(value) }
    return (
      <SidebarLink
        text={textValue}
        linkTo={linkTo}
        tooltipText={tooltip}
      />
    )
  }

  return (
    <SidebarSection
      title={propSchemaEx.title}
      copyText={textValue}
      key={propName}
    >
      {value
        ? renderSidebarLink(textValue, value, tooltip)
        : <SidebarText text={textValue} />
      }
    </SidebarSection>
  )
}

const getDateTimeSection = (propSchemaEx, propName, value, userTimeZone) => {
  const textValue = userTimeZoneFormat(userTimeZone, value)

  return (
    <SidebarSection
      title={propSchemaEx.title}
      tooltipText={propSchemaEx.description}
      copyText={textValue}
      key={propName}
    >
      <SidebarText text={textValue} />
    </SidebarSection>
  )
}

// returns one ore multiple sidebar section for specified value of the triggerProp
const getDependentSections = (triggerProp, value, activeTabId, values, userTimeZone) => {
  const dependentSchemaEx = triggerProp.propsByValues[value]
  const dependentSections = generateSections(activeTabId, values, dependentSchemaEx, userTimeZone)

  return dependentSections
}

const getTableSection = (propSchemaEx, propName, value) => {
  return (
    <SidebarSectionTable
      title={propSchemaEx.title}
      values={value}
      columnsOrder={propSchemaEx.columnsOrder}
      columns={propSchemaEx.columns}
      key={propName}
    />
  )
}

const generateSections = (activeTabId, values, schemaEx, userTimeZone) => {
  if (!schemaEx) {
    return
  }

  // iterate props
  const resProps = schemaEx.propsOrder.map(propName => {
    const prop = schemaEx.props[propName]
    const type = prop.type
    const isTable = Boolean(type === 'array' && prop.format === 'table')
    const isTextArray = Boolean(type === 'array' && !isTable && !prop.valuesMap)
    const isBadgesArray = Boolean(type === 'array' && !isTable && prop.valuesMap)

    // prop may exist in the scheme but data value may be absent
    const value = values ? values[propName] : null

    let resSection = null

    if (prop.format === 'internal_link') {
      resSection = getOneOfLinkSection(prop, propName, value)
    } else if (prop.format === 'color') {
      resSection = getColorSection(prop, propName, value)
    } else if (prop.format === 'datetime') {
      resSection = getDateTimeSection(prop, propName, value, userTimeZone)
    } else if (type === 'string' || (type === 'readOnlyField' && prop.originalType !== 'boolean') || type === 'integer' || isTextArray) {
      resSection = getTextSection(prop, propName, value)
    } else if (isBadgesArray) {
      resSection = getBadgesSection(prop, propName, value)
    } else if (type === 'image') {
      resSection = getImageSection(prop, propName, value)
    } else if (type === 'boolean' || prop.originalType === 'boolean') {
      resSection = getBoolSection(prop, propName, value)
    } else if (isTable) {
      resSection = getTableSection(prop, propName, value)
    }

    if (prop.propsByValues) {
      const dependentSections = getDependentSections(prop, value, activeTabId, values, userTimeZone)
      resSection = (
        <Fragment key={propName}>
          {resSection}
          {dependentSections}
        </Fragment>
      )
    }

    return resSection
  })

  // iterate groups
  const resGroups = (schemaEx.groupsOrder || []).map(groupPropName => {
    const groupSchemaEx = schemaEx.groups[groupPropName]
    const groupValues = values[groupPropName]

    return (
      <Fragment key={groupPropName}>
        <SidebarSectionGroup>
          <SidebarSectionGroupTitle title={groupSchemaEx.title} />
          {generateSections(activeTabId, groupValues, groupSchemaEx, userTimeZone)}
        </SidebarSectionGroup>
      </Fragment>
    )
  })

  return resProps.concat(resGroups)
}

const SidebarSectionsBySchema = ({
  activeTabId = 'general', values, schemaEx, additionalTabs, userTimeZone,
}) => {
  const tabSchemaEx = schemaEx.tabs[activeTabId]

  let result

  // render props for specific tabs (non-additional)
  if (tabSchemaEx) {
    const tabValues = (activeTabId === 'general') ? values : values[activeTabId]
    result = generateSections(activeTabId, tabValues, tabSchemaEx, userTimeZone)
  }

  // additional custom tab is active, render related component as it was passed
  if (additionalTabs?.[activeTabId]) {
    result = additionalTabs[activeTabId]
  }

  return result
}

SidebarSectionsBySchema.propTypes = {
  activeTabId: PropTypes.string,
  values: PropTypes.object.isRequired,
  schemaEx: PropTypes.object.isRequired,
  additionalTabs: PropTypes.object,
  userTimeZone: PropTypes.string,
}

export default SidebarSectionsBySchema
